import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PoolIcon from '@mui/icons-material/Pool';
import HistoryIcon from '@mui/icons-material/History';
import EmptyState from '../components/common/EmptyState';
import { useSnackbar } from '../components/common/SnackbarProvider';
import { useIsMobile } from '../hooks/useIsMobile';
import swimService from '../api/services/swimService';
import { AttendanceStatus, SwimAttendance, SwimEnrollment } from '../types/swim';

const statusColor: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  registered: 'success',
  waitlisted: 'warning',
  cancelled: 'default',
  completed: 'info',
};

const attendanceColor: Record<AttendanceStatus, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  present: 'success',
  late: 'warning',
  absent: 'error',
  excused: 'info',
  makeup: 'info',
};

const attendanceLabel: Record<AttendanceStatus, string> = {
  present: 'Present',
  late: 'Late',
  absent: 'Absent',
  excused: 'Excused',
  makeup: 'Makeup',
};

const ATTENDED_STATUSES: AttendanceStatus[] = ['present', 'late', 'makeup'];

// Mon=0..Sun=6 to match Python's date.weekday() and the program's recurrence_pattern.days.
const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function formatRecordWhen(record: SwimAttendance): string {
  if (!record.session_date) {
    return new Date(record.marked_at).toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }
  const [y, m, d] = record.session_date.split('-').map(Number);
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  const dateLabel = dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  if (!record.start_time) return dateLabel;
  const [hh, mm] = record.start_time.split(':').map(Number);
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const hr = hh % 12 || 12;
  return `${dateLabel} · ${hr}:${String(mm ?? 0).padStart(2, '0')} ${ampm}`;
}

function sortByEnrolledDesc(rows: SwimEnrollment[]): SwimEnrollment[] {
  return [...rows].sort((a, b) => (b.enrolled_at || '').localeCompare(a.enrolled_at || ''));
}

const MyEnrollmentsPage: React.FC = () => {
  const snackbar = useSnackbar();
  const isMobile = useIsMobile();
  const [items, setItems] = useState<SwimEnrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [completedOpen, setCompletedOpen] = useState(false);
  const [cancelledOpen, setCancelledOpen] = useState(false);

  // Weekly availability dialog state
  const [availabilityFor, setAvailabilityFor] = useState<SwimEnrollment | null>(null);
  const [selectedDays, setSelectedDays] = useState<Set<number>>(new Set());
  const [savingDays, setSavingDays] = useState(false);
  const [loadingDays, setLoadingDays] = useState(false);

  // Per-enrollment attendance history (lazy-loaded on first expand).
  const [expandedAttendance, setExpandedAttendance] = useState<Set<string>>(new Set());
  const [attendanceByEnrollment, setAttendanceByEnrollment] = useState<Record<string, SwimAttendance[]>>({});
  const [attendanceLoading, setAttendanceLoading] = useState<Set<string>>(new Set());

  async function toggleAttendance(enrollmentId: string) {
    const isOpen = expandedAttendance.has(enrollmentId);
    setExpandedAttendance((prev) => {
      const next = new Set(prev);
      if (isOpen) next.delete(enrollmentId);
      else next.add(enrollmentId);
      return next;
    });
    // Fetch on first open only; subsequent toggles reuse the cached list.
    if (!isOpen && !attendanceByEnrollment[enrollmentId]) {
      setAttendanceLoading((prev) => new Set(prev).add(enrollmentId));
      try {
        const res = await swimService.getEnrollmentAttendance(enrollmentId);
        setAttendanceByEnrollment((prev) => ({ ...prev, [enrollmentId]: res.data ?? [] }));
      } catch (e) {
        snackbar.error(e instanceof Error ? e.message : 'Failed to load attendance');
      } finally {
        setAttendanceLoading((prev) => {
          const next = new Set(prev);
          next.delete(enrollmentId);
          return next;
        });
      }
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const rows = await swimService.myEnrollments();
      setItems(rows);
    } catch (e) {
      snackbar.error(e instanceof Error ? e.message : 'Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  }

  async function cancel(id: string) {
    // The prompt doubles as the confirm step (Cancel on the prompt = abort) and captures an
    // optional reason so coaches know why the swimmer dropped.
    const reason = window.prompt(
      'Cancel this enrollment?\n\nOptionally, let the coach know why (moving, schedule conflict, injury, …). Leave blank to skip.',
    );
    if (reason === null) return;  // backed out of the prompt
    setCancelling(id);
    try {
      await swimService.cancelEnrollment(id, reason.trim() || undefined);
      await load();
      snackbar.success('Enrollment cancelled');
    } catch (e) {
      snackbar.error(e instanceof Error ? e.message : 'Cancel failed');
    } finally {
      setCancelling(null);
    }
  }

  async function openAvailability(enrollment: SwimEnrollment) {
    const programDays = enrollment.program?.recurrence_pattern?.days ?? [];
    // Open immediately with all-checked as the optimistic default so the
    // dialog feels snappy; replace with the server's inferred state as
    // soon as the GET resolves.
    setSelectedDays(new Set(programDays));
    setAvailabilityFor(enrollment);
    setLoadingDays(true);
    try {
      const result = await swimService.getWeeklyAvailability(enrollment.id);
      setSelectedDays(new Set(result.available_days));
    } catch {
      // Non-blocking: fall back to optimistic default already set.
    } finally {
      setLoadingDays(false);
    }
  }

  function toggleDay(day: number) {
    setSelectedDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  }

  async function saveAvailability() {
    if (!availabilityFor) return;
    setSavingDays(true);
    try {
      const result = await swimService.setWeeklyAvailability(
        availabilityFor.id,
        Array.from(selectedDays).sort((a, b) => a - b),
      );
      setAvailabilityFor(null);
      snackbar.success(
        result.skipped === 0
          ? `Set to attend all ${result.total} upcoming sessions`
          : `Updated — ${result.planned} planned, ${result.skipped} skipped`,
      );
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Failed to update availability');
    } finally {
      setSavingDays(false);
    }
  }

  const { active, completed, cancelled } = useMemo(() => {
    const a: SwimEnrollment[] = [];
    const co: SwimEnrollment[] = [];
    const ca: SwimEnrollment[] = [];
    for (const e of items) {
      if (e.status === 'completed') co.push(e);
      else if (e.status === 'cancelled') ca.push(e);
      else a.push(e);
    }
    return {
      active: sortByEnrolledDesc(a),
      completed: sortByEnrolledDesc(co),
      cancelled: sortByEnrolledDesc(ca),
    };
  }, [items]);

  function renderCard(e: SwimEnrollment) {
    const swimmerName = e.enrollee
      ? `${e.enrollee.first_name ?? ''} ${e.enrollee.last_name ?? ''}`.trim() || 'Swimmer'
      : 'Swimmer';
    const isCancellable = e.status === 'registered' || e.status === 'waitlisted';
    const isActive = isCancellable;
    const hasSchedule = (e.program?.recurrence_pattern?.days?.length ?? 0) > 0;
    const dim = e.status === 'cancelled' || e.status === 'completed';
    const cardClickable = isActive && hasSchedule;

    return (
      <Card
        key={e.id}
        variant="outlined"
        onClick={cardClickable ? () => void openAvailability(e) : undefined}
        onKeyDown={cardClickable ? (ev) => {
          if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault();
            void openAvailability(e);
          }
        } : undefined}
        role={cardClickable ? 'button' : undefined}
        tabIndex={cardClickable ? 0 : undefined}
        aria-label={cardClickable ? `Set weekly availability for ${swimmerName}` : undefined}
        sx={{
          opacity: dim ? 0.85 : 1,
          cursor: cardClickable ? 'pointer' : 'default',
          transition: 'background-color 150ms ease, border-color 150ms ease',
          '&:hover': cardClickable
            ? { backgroundColor: 'rgba(0,0,0,0.02)', borderColor: 'primary.main' }
            : undefined,
          '&:focus-visible': cardClickable
            ? { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 }
            : undefined,
        }}
      >
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            spacing={{ xs: 1, sm: 2 }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    textDecoration: e.status === 'cancelled' ? 'line-through' : 'none',
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {swimmerName} — {e.program?.name ?? 'Program'}
                </Typography>
                {cardClickable && (
                  <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
                )}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                <Chip
                  size="small"
                  label={e.status}
                  color={statusColor[e.status] ?? 'default'}
                />
                {e.status === 'registered' && !e.confirmed && (
                  <Chip
                    size="small"
                    label="Pending confirmation"
                    color="warning"
                    variant="outlined"
                  />
                )}
                {e.waitlist_position != null && (
                  <Chip size="small" label={`Waitlist #${e.waitlist_position}`} color="warning" />
                )}
              </Stack>
            </Box>
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, flexShrink: 0 }}>
              <Typography variant="body2"><strong>${Number(e.amount_due).toFixed(2)}</strong></Typography>
              <Typography variant="caption" color="text.secondary">
                enrolled {e.enrolled_at ? new Date(e.enrolled_at).toLocaleDateString() : '—'}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
        {isCancellable && (
          <CardActions sx={{ justifyContent: 'flex-end' }}>
            <Button
              color="error"
              disabled={cancelling === e.id}
              onClick={(ev) => {
                ev.stopPropagation();
                void cancel(e.id);
              }}
            >
              {cancelling === e.id ? 'Cancelling…' : 'Cancel'}
            </Button>
          </CardActions>
        )}
        {renderAttendanceFooter(e)}
      </Card>
    );
  }

  function renderAttendanceFooter(e: SwimEnrollment) {
    const isOpen = expandedAttendance.has(e.id);
    const isLoading = attendanceLoading.has(e.id);
    const records = attendanceByEnrollment[e.id];
    const attended = records?.filter((r) => ATTENDED_STATUSES.includes(r.status)).length ?? 0;
    const total = records?.length ?? 0;
    return (
      <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
        <Box
          role="button"
          tabIndex={0}
          onClick={(ev) => {
            ev.stopPropagation();
            void toggleAttendance(e.id);
          }}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter' || ev.key === ' ') {
              ev.preventDefault();
              ev.stopPropagation();
              void toggleAttendance(e.id);
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            cursor: 'pointer',
            userSelect: 'none',
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)', color: 'primary.main' },
          }}
        >
          <HistoryIcon fontSize="small" />
          <Typography variant="caption" sx={{ flex: 1 }}>
            Attendance
            {records && total > 0 && ` · ${attended} of ${total} attended`}
            {records && total === 0 && ' · no records yet'}
          </Typography>
          {isLoading && <CircularProgress size={14} />}
          {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Box>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Box sx={{ px: 2, py: 1 }}>
            {!records && !isLoading && (
              <Typography variant="caption" color="text.secondary">
                Loading…
              </Typography>
            )}
            {records && records.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No sessions marked yet. Records appear after a coach takes attendance.
              </Typography>
            )}
            {records && records.length > 0 && (
              <Stack spacing={0.75}>
                {records.map((r) => (
                  <Stack
                    key={r.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ flexWrap: 'wrap', rowGap: 0.25 }}
                  >
                    <Chip
                      size="small"
                      label={attendanceLabel[r.status]}
                      color={attendanceColor[r.status]}
                      variant={ATTENDED_STATUSES.includes(r.status) ? 'filled' : 'outlined'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                    <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }}>
                      {formatRecordWhen(r)}
                    </Typography>
                    {r.notes && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ width: '100%', pl: '76px', fontStyle: 'italic' }}
                      >
                        {r.notes}
                      </Typography>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  }

  function renderCollapsibleSection(
    title: string,
    rows: SwimEnrollment[],
    open: boolean,
    onToggle: () => void,
  ) {
    if (rows.length === 0) return null;
    return (
      <Box>
        <Box
          onClick={onToggle}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggle();
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            py: 1.5,
            borderTop: '1px solid rgba(0,0,0,0.08)',
            userSelect: 'none',
            '&:hover': { color: 'primary.main' },
          }}
        >
          <Typography variant="overline" sx={{ flex: 1, color: 'inherit' }}>
            {title} · {rows.length}
          </Typography>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Box>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Stack spacing={2} sx={{ mt: 1.5, mb: 1 }}>
            {rows.map(renderCard)}
          </Stack>
        </Collapse>
      </Box>
    );
  }

  const dialogSwimmerName = availabilityFor?.enrollee
    ? `${availabilityFor.enrollee.first_name ?? ''} ${availabilityFor.enrollee.last_name ?? ''}`.trim()
    : '';
  const programDays = availabilityFor?.program?.recurrence_pattern?.days ?? [];

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">My Swim Enrollments</Typography>
        <Typography variant="body2" color="text.secondary">
          Track signups for everyone on your account. Tap a swimmer's name to set which days they'll attend each week.
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<PoolIcon />}
          title="No swim enrollments yet"
          body="Browse Swim Classes to sign up a swimmer for a session."
        />
      ) : (
        <Box>
          {active.length > 0 ? (
            <Stack spacing={2}>
              {active.map(renderCard)}
            </Stack>
          ) : (
            <EmptyState
              size="sm"
              icon={<PoolIcon />}
              title="No active enrollments"
              body="Browse Swim Classes to enroll a swimmer in an upcoming session."
            />
          )}

          {(completed.length > 0 || cancelled.length > 0) && (
            <Box sx={{ mt: 4 }}>
              {renderCollapsibleSection(
                'Completed',
                completed,
                completedOpen,
                () => setCompletedOpen((o) => !o),
              )}
              {renderCollapsibleSection(
                'Cancelled',
                cancelled,
                cancelledOpen,
                () => setCancelledOpen((o) => !o),
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Weekly availability dialog */}
      <Dialog
        open={availabilityFor !== null}
        onClose={() => !savingDays && setAvailabilityFor(null)}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ flex: 1 }}>
              Weekly availability
              {dialogSwimmerName && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {dialogSwimmerName} · {availabilityFor?.program?.name}
                </Typography>
              )}
            </Box>
            {loadingDays && <CircularProgress size={18} />}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Check the days you plan to attend each week. Unchecked days will be marked as skip
            for every upcoming session — you can still un-skip individual sessions on the calendar.
          </Typography>
          <Stack spacing={0.5}>
            {programDays.length === 0 ? (
              <Alert severity="info">This class has no scheduled days yet.</Alert>
            ) : (
              programDays.map((d) => (
                <FormControlLabel
                  key={d}
                  control={
                    <Checkbox
                      checked={selectedDays.has(d)}
                      onChange={() => toggleDay(d)}
                    />
                  }
                  label={DAY_LABELS[d] ?? `Day ${d}`}
                />
              ))
            )}
          </Stack>
          {programDays.length > 0 && selectedDays.size === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              All days unchecked. Saving will mark every upcoming session as skip.
            </Alert>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            '& > button': { width: { xs: '100%', sm: 'auto' }, ml: { xs: '0 !important', sm: undefined } },
          }}
        >
          <Button onClick={() => setAvailabilityFor(null)} disabled={savingDays}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void saveAvailability()}
            disabled={savingDays || programDays.length === 0}
          >
            {savingDays ? 'Saving…' : 'Save days'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyEnrollmentsPage;
